import { get, onChildAdded, onChildChanged, onChildRemoved } from "firebase/database";
import {
  resolveRtdbRefPath,
  trackRtdbListenerClose,
  trackRtdbListenerOpen,
  trackRtdbLogicalSnapshot,
} from "./rtdbTrafficMonitor.js";

function defaultMapItem(id, value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return { id, ...value };
  }
  return { id, value };
}

export function listenRtdbListIncremental(queryRef, options = {}) {
  const mapItem = typeof options.mapItem === "function" ? options.mapItem : defaultMapItem;
  const sortItems = typeof options.sortItems === "function" ? options.sortItems : null;
  const isItemEqual =
    typeof options.isItemEqual === "function" ? options.isItemEqual : null;
  const onData = typeof options.onData === "function" ? options.onData : () => {};
  const onError = typeof options.onError === "function" ? options.onError : () => {};
  const onReady = typeof options.onReady === "function" ? options.onReady : null;
  const emitDebounceMs = Number.isFinite(options.emitDebounceMs)
    ? Math.max(0, options.emitDebounceMs)
    : 16;

  const byId = new Map();
  let stopped = false;
  let ready = false;
  let emitTimer = null;
  const basePath = resolveRtdbRefPath(queryRef);
  const listenerToken = trackRtdbListenerOpen(basePath);
  let unsubAdded = null;
  let unsubChanged = null;
  let unsubRemoved = null;

  const emit = () => {
    if (stopped) return;
    let arr = Array.from(byId.values());
    if (sortItems) arr = sortItems(arr);
    onData(arr);
  };

  const scheduleEmit = () => {
    if (stopped) return;
    if (emitTimer) return;
    emitTimer = setTimeout(() => {
      emitTimer = null;
      emit();
    }, emitDebounceMs);
  };

  const upsertFromSnapshot = (snap) => {
    if (!snap?.key) return;
    trackRtdbLogicalSnapshot({
      path: `${basePath}/${snap.key}`,
      payload: snap.val(),
      source: "incremental:child",
    });
    const nextItem = mapItem(snap.key, snap.val());
    const prevItem = byId.get(snap.key);
    if (isItemEqual && prevItem && isItemEqual(prevItem, nextItem)) {
      return false;
    }
    byId.set(snap.key, nextItem);
    return true;
  };

  const removeFromSnapshot = (snap) => {
    if (!snap?.key) return;
    byId.delete(snap.key);
  };

  const attachRealtimeListeners = () => {
    if (stopped) return;
    if (unsubAdded || unsubChanged || unsubRemoved) return;

    unsubAdded = onChildAdded(
      queryRef,
      (snap) => {
        if (upsertFromSnapshot(snap) !== false) {
          scheduleEmit();
        }
      },
      (err) => onError(err)
    );

    unsubChanged = onChildChanged(
      queryRef,
      (snap) => {
        if (upsertFromSnapshot(snap) !== false) {
          scheduleEmit();
        }
      },
      (err) => onError(err)
    );

    unsubRemoved = onChildRemoved(
      queryRef,
      (snap) => {
        removeFromSnapshot(snap);
        scheduleEmit();
      },
      (err) => onError(err)
    );
  };

  get(queryRef)
    .then((snap) => {
      if (stopped) return;
      const raw = snap?.val() || {};
      trackRtdbLogicalSnapshot({
        path: basePath,
        payload: raw,
        itemsCount:
          raw && typeof raw === "object" ? Object.keys(raw).length : 0,
        source: "incremental:get",
      });
      if (raw && typeof raw === "object") {
        Object.entries(raw).forEach(([id, value]) => {
          byId.set(id, mapItem(id, value));
        });
      }
      if (!ready) {
        ready = true;
        if (onReady) onReady();
      }
      emit();
      attachRealtimeListeners();
    })
    .catch((err) => {
      if (stopped) return;
      onError(err);
      if (!ready) {
        ready = true;
        if (onReady) onReady();
      }
      attachRealtimeListeners();
    });

  return () => {
    stopped = true;
    trackRtdbListenerClose(listenerToken);
    if (emitTimer) {
      clearTimeout(emitTimer);
      emitTimer = null;
    }
    try {
      unsubAdded?.();
    } catch {}
    try {
      unsubChanged?.();
    } catch {}
    try {
      unsubRemoved?.();
    } catch {}
  };
}
